import { useId, useMemo, useState, type ChangeEvent, type FocusEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, Plus, Trash2, Webhook } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Input } from "@/components/ui/Input";
import { CopyableRequest } from "@/components/studio/CopyableRequest";
import type { Application } from "@/services/api/applications";
import {
  workflowTriggersAPI,
  type WorkflowTrigger,
  type WorkflowTriggerCall,
  type WorkflowTriggerInputMapping,
  type WorkflowTriggerResponseMode,
  type UpdateWorkflowTriggerInput,
} from "@/services/api/workflowTriggers";
import { toast } from "@/providers/NotificationProvider";

/**
 * Workflow › API & triggers › Triggers › Incoming webhook
 * (pilote-maurice wiki/architecture/workflow-webhook-trigger.md § 10, OSR-15):
 * create a trigger on an Application that publishes the Workflow, show the
 * secret once with the URL and a signed curl example, list the triggers with
 * their state, mapping, response mode, rate limit and last call, edit them
 * (mapping fields and constants, response, per-minute limit: AGENT-842),
 * disable / enable / rotate / revoke (confirmed), and open the call journal
 * from the row.
 */

export const WEBHOOK_SECRET_ENV = "AGENTMAURICE_WEBHOOK_SECRET";

export function webhookTriggerURL(publicURL: string, path: string): string {
  return `${publicURL.replace(/\/+$/, "")}${path}`;
}

/** Signed call example: HMAC-SHA256 of "v1:{timestamp}:{body}". */
export function buildSignedWebhookCurl(url: string, body: unknown): string {
  const payload = JSON.stringify(body ?? {}).replace(/'/g, "'\\''");
  return [
    `BODY='${payload}'`,
    "TS=$(date +%s)",
    `SIG=$(printf 'v1:%s:%s' "$TS" "$BODY" | openssl dgst -sha256 -hmac "$${WEBHOOK_SECRET_ENV}" | sed 's/^.* //')`,
    `curl -sS -X POST "${url}" \\`,
    '  -H "Content-Type: application/json" \\',
    '  -H "X-AgentMaurice-Timestamp: $TS" \\',
    '  -H "X-AgentMaurice-Signature: v1=$SIG" \\',
    '  --data-binary "$BODY"',
  ].join("\n");
}

/** Parses "field = /json/pointer" lines into the pointers mapping. */
export function parsePointerLines(
  text: string,
): { fields: Record<string, string>; invalidLine: string | null } {
  const fields: Record<string, string> = {};
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const separator = line.indexOf("=");
    const field = separator > 0 ? line.slice(0, separator).trim() : "";
    const pointer = separator > 0 ? line.slice(separator + 1).trim() : "";
    if (!field || (pointer !== "" && !pointer.startsWith("/"))) {
      return { fields, invalidLine: line };
    }
    fields[field] = pointer;
  }
  return { fields, invalidLine: null };
}

/** Per-trigger limit bounds (§ 9): default 60, capped by the instance at 600. */
export const WEBHOOK_RATE_LIMIT_DEFAULT = 60;
export const WEBHOOK_RATE_LIMIT_MAX = 600;

/**
 * Reads a constant typed in the form: a JSON literal (number, true/false,
 * null, "quoted text", object, array) keeps its type, anything else is text.
 */
export function parseConstantValue(raw: string): unknown {
  const value = raw.trim();
  if (value === "") return "";
  try {
    return JSON.parse(value);
  } catch {
    return raw;
  }
}

/** Inverse of parseConstantValue: text that would read as JSON is quoted. */
export function formatConstantValue(value: unknown): string {
  if (typeof value === "string") {
    if (value.trim() === "") return JSON.stringify(value);
    try {
      JSON.parse(value);
      return JSON.stringify(value);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value ?? null);
}

export type ConstantRow = { key: string; value: string };

/** Builds the constants map; the first invalid key is reported. */
export function constantsFromRows(rows: ConstantRow[]): {
  constants: Record<string, unknown>;
  invalidKey: string | null;
} {
  const constants: Record<string, unknown> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key && row.value.trim() === "") continue;
    if (!key || key.startsWith("__context.") || key in constants) {
      return { constants, invalidKey: key || row.value };
    }
    constants[key] = parseConstantValue(row.value);
  }
  return { constants, invalidKey: null };
}

export type TriggerDraft = {
  name: string;
  mappingMode: WorkflowTriggerInputMapping["mode"];
  pointerLines: string;
  constants: ConstantRow[];
  responseMode: WorkflowTriggerResponseMode;
  syncTimeout: string;
  rateLimit: string;
};

export const emptyTriggerDraft: TriggerDraft = {
  name: "",
  mappingMode: "body",
  pointerLines: "",
  constants: [],
  responseMode: "async",
  syncTimeout: "10",
  rateLimit: String(WEBHOOK_RATE_LIMIT_DEFAULT),
};

export function draftFromTrigger(trigger: WorkflowTrigger): TriggerDraft {
  const mapping = trigger.input_mapping ?? { mode: "body" };
  return {
    name: trigger.name ?? "",
    mappingMode: mapping.mode === "pointers" ? "pointers" : "body",
    pointerLines: Object.entries(mapping.fields ?? {})
      .map(([field, pointer]) => `${field} = ${pointer}`)
      .join("\n"),
    constants: Object.entries(mapping.constants ?? {}).map(([key, value]) => ({
      key,
      value: formatConstantValue(value),
    })),
    responseMode: trigger.response_mode === "sync" ? "sync" : "async",
    syncTimeout: String(trigger.sync_timeout_seconds || 10),
    rateLimit: String(trigger.rate_limit_per_minute || WEBHOOK_RATE_LIMIT_DEFAULT),
  };
}

/** Validates a draft and renders the body shared by creation and edition. */
export function evaluateDraft(draft: TriggerDraft) {
  const pointers = parsePointerLines(draft.pointerLines);
  const constants = constantsFromRows(draft.constants);
  const timeout = Number(draft.syncTimeout);
  const rateLimit = Number(draft.rateLimit);
  const timeoutValid =
    draft.responseMode === "async" ||
    (Number.isInteger(timeout) && timeout >= 1 && timeout <= 30);
  const rateLimitValid =
    Number.isInteger(rateLimit) && rateLimit >= 1 && rateLimit <= WEBHOOK_RATE_LIMIT_MAX;
  const mappingValid =
    draft.mappingMode === "body" ||
    (pointers.invalidLine === null &&
      constants.invalidKey === null &&
      Object.keys(pointers.fields).length + Object.keys(constants.constants).length > 0);
  const inputMapping: WorkflowTriggerInputMapping =
    draft.mappingMode === "body"
      ? { mode: "body" }
      : {
          mode: "pointers",
          ...(Object.keys(pointers.fields).length > 0 ? { fields: pointers.fields } : {}),
          ...(Object.keys(constants.constants).length > 0
            ? { constants: constants.constants }
            : {}),
        };
  const body: UpdateWorkflowTriggerInput = {
    name: draft.name.trim(),
    input_mapping: inputMapping,
    response_mode: draft.responseMode,
    ...(draft.responseMode === "sync" ? { sync_timeout_seconds: timeout } : {}),
    rate_limit_per_minute: rateLimit,
  };
  return {
    pointers,
    constants,
    timeoutValid,
    rateLimitValid,
    mappingValid,
    valid: timeoutValid && rateLimitValid && mappingValid,
    body,
  };
}

function useDateFormatter() {
  const { i18n } = useTranslation();
  return (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getFullYear() < 2) return null;
    return new Intl.DateTimeFormat(i18n.language || undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  };
}

type Revealed = { name: string; url: string; secret: string; rotated: boolean };
type PendingAction = { trigger: WorkflowTrigger; kind: "revoke" | "rotate" };

const statusVariant = {
  active: "success",
  disabled: "warning",
  revoked: "muted",
} as const;

const outcomeVariant = {
  accepted: "success",
  duplicate: "info",
  rejected: "destructive",
} as const;

export function WorkflowWebhookTriggersSection({
  organizationId,
  agentId,
  workflowId,
  applications,
  applicationsLoading = false,
  publicURL,
  inputExample,
}: {
  organizationId: string;
  agentId: string;
  workflowId: string;
  /** Applications whose public surface publishes this Workflow on this Agent. */
  applications: Application[];
  applicationsLoading?: boolean;
  publicURL: string;
  inputExample: unknown;
}) {
  const { t } = useTranslation();
  const formatDate = useDateFormatter();
  const formId = useId();
  const queryClient = useQueryClient();
  const applicationIds = useMemo(
    () => applications.map((application) => application.id).sort(),
    [applications],
  );
  const queryKey = [
    "workflow-triggers",
    organizationId,
    agentId,
    workflowId,
    applicationIds.join(","),
  ];
  const triggersQuery = useQuery({
    queryKey,
    enabled: Boolean(organizationId) && applicationIds.length > 0,
    queryFn: async () => {
      const lists = await Promise.all(
        applicationIds.map((applicationId) =>
          workflowTriggersAPI.list(organizationId, applicationId, workflowId),
        ),
      );
      return lists.flat().filter((trigger) => trigger.agent_id === agentId);
    },
  });
  const triggers = triggersQuery.data ?? [];
  const applicationName = (applicationId: string) => {
    const match = applications.find((item) => item.id === applicationId);
    return match ? match.display_name || match.key : applicationId;
  };

  const [draft, setDraft] = useState<TriggerDraft>(emptyTriggerDraft);
  const [applicationId, setApplicationId] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [journalFor, setJournalFor] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ triggerId: string; draft: TriggerDraft } | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const selectedApplicationId = applications.some(
    (application) => application.id === applicationId,
  )
    ? applicationId
    : (applications[0]?.id ?? "");
  const evaluated = evaluateDraft(draft);
  const canSubmit = !creating && Boolean(selectedApplicationId) && evaluated.valid;

  const reveal = (
    triggerName: string,
    path: string,
    secret: string,
    rotated: boolean,
  ) => {
    setRevealed({
      name: triggerName,
      url: webhookTriggerURL(publicURL, path),
      secret,
      rotated,
    });
    setCopied(false);
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const submit = async () => {
    if (!canSubmit) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await workflowTriggersAPI.create(
        organizationId,
        selectedApplicationId,
        {
          ...evaluated.body,
          name: evaluated.body.name ?? "",
          workflow_id: workflowId,
          agent_id: agentId,
        },
      );
      reveal(
        created.trigger.name || t("workflowApi.webhook.unnamed"),
        created.path,
        created.secret,
        false,
      );
      setDraft(emptyTriggerDraft);
      await refresh();
    } catch (error) {
      setCreateError(
        error instanceof Error && error.message
          ? error.message
          : t("workflowApi.webhook.createFailed"),
      );
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (trigger: WorkflowTrigger) => {
    setEditError(null);
    setEditing({ triggerId: trigger.trigger_id, draft: draftFromTrigger(trigger) });
  };

  const saveEdit = async (trigger: WorkflowTrigger) => {
    if (!editing || editing.triggerId !== trigger.trigger_id) return;
    const result = evaluateDraft(editing.draft);
    if (!result.valid) return;
    setSaving(true);
    setEditError(null);
    try {
      await workflowTriggersAPI.update(
        organizationId,
        trigger.application_id,
        trigger.trigger_id,
        result.body,
      );
      toast.success(t("workflowApi.webhook.saved"));
      setEditing(null);
      await refresh();
    } catch (error) {
      setEditError(
        error instanceof Error && error.message
          ? error.message
          : t("workflowApi.webhook.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (
    trigger: WorkflowTrigger,
    action: "disable" | "enable" | "rotate" | "revoke",
  ) => {
    setBusy(trigger.trigger_id);
    try {
      if (action === "disable") {
        await workflowTriggersAPI.disable(
          organizationId,
          trigger.application_id,
          trigger.trigger_id,
        );
      } else if (action === "enable") {
        await workflowTriggersAPI.enable(
          organizationId,
          trigger.application_id,
          trigger.trigger_id,
        );
      } else if (action === "rotate") {
        const rotated = await workflowTriggersAPI.rotateSecret(
          organizationId,
          trigger.application_id,
          trigger.trigger_id,
        );
        reveal(
          trigger.name || t("workflowApi.webhook.unnamed"),
          rotated.path,
          rotated.secret,
          true,
        );
      } else {
        await workflowTriggersAPI.revoke(
          organizationId,
          trigger.application_id,
          trigger.trigger_id,
        );
        toast.success(t("workflowApi.webhook.revoked"));
      }
      setPending(null);
      await refresh();
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t("workflowApi.webhook.actionFailed"),
      );
    } finally {
      setBusy(null);
    }
  };

  const copySecret = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.secret);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section
      className="space-y-4 rounded-lg border border-border bg-card p-4"
      data-testid="workflow-webhook-triggers"
    >
      <div className="space-y-1">
        <h3 className="flex items-center gap-2 font-semibold">
          <Webhook className="h-4 w-4" aria-hidden="true" />
          {t("workflowApi.webhookTitle")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("workflowApi.webhook.intro")}
        </p>
      </div>

      {revealed ? (
        <section
          role="status"
          className="space-y-3 rounded-lg border border-warning/30 bg-warning-soft p-4"
          data-testid="workflow-webhook-revealed"
        >
          <p className="font-medium text-foreground">
            {revealed.rotated
              ? t("workflowApi.webhook.rotatedTitle", { name: revealed.name })
              : t("workflowApi.webhook.revealedTitle", { name: revealed.name })}
          </p>
          <p className="text-sm text-foreground-soft">
            {t("workflowApi.webhook.revealedWarning")}
          </p>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-foreground">
              {t("workflowApi.webhook.urlLabel")}
            </span>
            <CopyableRequest
              value={revealed.url}
              testId="workflow-webhook-revealed-url"
            />
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              readOnly
              value={revealed.secret}
              aria-label={t("workflowApi.webhook.secretLabel")}
              className="font-mono text-xs"
              data-testid="workflow-webhook-revealed-secret"
              onFocus={(event: FocusEvent<HTMLInputElement>) =>
                event.currentTarget.select()
              }
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => void copySecret()}
              data-testid="workflow-webhook-copy-secret"
            >
              <Copy className="mr-2 h-4 w-4" />
              {copied ? t("apiKeys.copied") : t("apiKeys.copy")}
            </Button>
          </div>
          <p className="text-sm text-foreground-soft">
            {t("workflowApi.webhook.curlIntro", { env: WEBHOOK_SECRET_ENV })}
          </p>
          <CopyableRequest
            value={buildSignedWebhookCurl(revealed.url, inputExample)}
            testId="workflow-webhook-revealed-curl"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setRevealed(null)}
            data-testid="workflow-webhook-dismiss"
          >
            {t("apiKeys.dismissRevealed")}
          </Button>
        </section>
      ) : null}

      {applications.length === 0 ? (
        <p
          className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground"
          data-testid="workflow-webhook-unpublished"
        >
          {applicationsLoading
            ? t("common.loading")
            : t("workflowApi.webhook.needsPublication")}
        </p>
      ) : (
        <form
          className="space-y-4 rounded-lg border border-border p-4"
          aria-labelledby={`${formId}-title`}
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <h4 id={`${formId}-title`} className="font-medium text-foreground">
            {t("workflowApi.webhook.createTitle")}
          </h4>
          <label className="block space-y-1 text-sm sm:max-w-sm">
            <span className="font-medium text-foreground">
              {t("workflowApi.applicationSelect")}
            </span>
            <select
              value={selectedApplicationId}
              onChange={(event) => setApplicationId(event.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="workflow-webhook-application"
            >
              {applications.map((application) => (
                <option key={application.id} value={application.id}>
                  {application.display_name || application.key}
                </option>
              ))}
            </select>
          </label>
          <WebhookTriggerFields
            draft={draft}
            onChange={setDraft}
            testIdPrefix="workflow-webhook"
          />
          {createError ? (
            <p role="alert" className="text-sm text-destructive">
              {createError}
            </p>
          ) : null}
          <Button
            type="submit"
            disabled={!canSubmit}
            data-testid="workflow-webhook-create"
          >
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("workflowApi.webhook.createButton")}
          </Button>
        </form>
      )}

      <section className="space-y-2">
        <h4 className="font-medium text-foreground">
          {t("workflowApi.webhook.listTitle")}
        </h4>
        {triggersQuery.isLoading && applicationIds.length > 0 ? (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : triggersQuery.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {t("workflowApi.webhook.loadFailed")}
          </p>
        ) : triggers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("workflowApi.webhook.empty")}
          </p>
        ) : (
          <ul className="space-y-2">
            {triggers.map((trigger) => {
              const lastCall = formatDate(trigger.last_call_at);
              const revoked = trigger.status === "revoked";
              const url = webhookTriggerURL(publicURL, trigger.path);
              const rowBusy = busy === trigger.trigger_id;
              return (
                <li
                  key={trigger.trigger_id}
                  className="space-y-3 rounded-lg border border-border p-3"
                  data-testid={`workflow-webhook-item-${trigger.trigger_id}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {trigger.name || t("workflowApi.webhook.unnamed")}
                    </p>
                    <Badge variant={statusVariant[trigger.status] ?? "outline"}>
                      {t(`workflowApi.webhook.status.${trigger.status}`)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {applicationName(trigger.application_id)}
                    </span>
                  </div>
                  {revoked ? null : (
                    <CopyableRequest
                      value={url}
                      testId={`workflow-webhook-url-${trigger.trigger_id}`}
                    />
                  )}
                  <dl className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                    {trigger.secret_hint ? (
                      <div>
                        <dt className="inline">{t("workflowApi.webhook.secretLabel")} : </dt>
                        <dd className="inline font-mono">{trigger.secret_hint}</dd>
                      </div>
                    ) : null}
                    <div data-testid={`workflow-webhook-last-call-${trigger.trigger_id}`}>
                      <dt className="inline">{t("workflowApi.webhook.lastCall")} : </dt>
                      <dd className="inline">
                        {lastCall ? (
                          <>
                            {lastCall}{" "}
                            {trigger.last_outcome ? (
                              <Badge
                                variant={
                                  outcomeVariant[
                                    trigger.last_outcome as keyof typeof outcomeVariant
                                  ] ?? "outline"
                                }
                              >
                                {t(`workflowApi.webhook.outcome.${trigger.last_outcome}`)}
                              </Badge>
                            ) : null}{" "}
                            {trigger.last_error_code ? (
                              <code>{trigger.last_error_code}</code>
                            ) : null}
                          </>
                        ) : (
                          t("workflowApi.webhook.neverCalled")
                        )}
                      </dd>
                    </div>
                    <div data-testid={`workflow-webhook-response-${trigger.trigger_id}`}>
                      <dt className="inline">{t("workflowApi.webhook.responseLabel")} : </dt>
                      <dd className="inline">
                        {trigger.response_mode === "sync"
                          ? t("workflowApi.webhook.responseSyncSummary", {
                              seconds: trigger.sync_timeout_seconds,
                            })
                          : t("workflowApi.webhook.responseAsyncSummary")}
                      </dd>
                    </div>
                    <div data-testid={`workflow-webhook-rate-${trigger.trigger_id}`}>
                      <dt className="inline">{t("workflowApi.webhook.rateLimitLabel")} : </dt>
                      <dd className="inline">
                        {t("workflowApi.webhook.rateLimitSummary", {
                          value: trigger.rate_limit_per_minute || WEBHOOK_RATE_LIMIT_DEFAULT,
                        })}
                      </dd>
                    </div>
                  </dl>
                  <WebhookMappingSummary trigger={trigger} />
                  <div className="flex flex-wrap gap-2">
                    {trigger.status === "active" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={rowBusy}
                        onClick={() => void runAction(trigger, "disable")}
                        data-testid={`workflow-webhook-disable-${trigger.trigger_id}`}
                      >
                        {t("workflowApi.webhook.disable")}
                      </Button>
                    ) : null}
                    {trigger.status === "disabled" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={rowBusy}
                        onClick={() => void runAction(trigger, "enable")}
                        data-testid={`workflow-webhook-enable-${trigger.trigger_id}`}
                      >
                        {t("workflowApi.webhook.enable")}
                      </Button>
                    ) : null}
                    {revoked ? null : (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={rowBusy || saving}
                          aria-expanded={editing?.triggerId === trigger.trigger_id}
                          onClick={() =>
                            editing?.triggerId === trigger.trigger_id
                              ? setEditing(null)
                              : startEdit(trigger)
                          }
                          data-testid={`workflow-webhook-edit-${trigger.trigger_id}`}
                        >
                          {t("workflowApi.webhook.edit")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={rowBusy}
                          onClick={() => setPending({ trigger, kind: "rotate" })}
                          data-testid={`workflow-webhook-rotate-${trigger.trigger_id}`}
                        >
                          {t("workflowApi.webhook.rotate")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          disabled={rowBusy}
                          onClick={() => setPending({ trigger, kind: "revoke" })}
                          data-testid={`workflow-webhook-revoke-${trigger.trigger_id}`}
                        >
                          {t("workflowApi.webhook.revoke")}
                        </Button>
                      </>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-expanded={journalFor === trigger.trigger_id}
                      onClick={() =>
                        setJournalFor((current) =>
                          current === trigger.trigger_id ? null : trigger.trigger_id,
                        )
                      }
                      data-testid={`workflow-webhook-journal-${trigger.trigger_id}`}
                    >
                      {t("workflowApi.webhook.journal")}
                    </Button>
                  </div>
                  {editing?.triggerId === trigger.trigger_id && !revoked ? (
                    <form
                      className="space-y-4 rounded-lg border border-border bg-muted/30 p-4"
                      aria-label={t("workflowApi.webhook.editTitle", {
                        name: trigger.name || t("workflowApi.webhook.unnamed"),
                      })}
                      data-testid={`workflow-webhook-edit-form-${trigger.trigger_id}`}
                      onSubmit={(event) => {
                        event.preventDefault();
                        void saveEdit(trigger);
                      }}
                    >
                      <p className="text-sm text-muted-foreground">
                        {t("workflowApi.webhook.editIntro")}
                      </p>
                      <WebhookTriggerFields
                        draft={editing.draft}
                        onChange={(next) =>
                          setEditing((current) =>
                            current && current.triggerId === trigger.trigger_id
                              ? { ...current, draft: next }
                              : current,
                          )
                        }
                        testIdPrefix="workflow-webhook-edit"
                      />
                      {editError ? (
                        <p role="alert" className="text-sm text-destructive">
                          {editError}
                        </p>
                      ) : null}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="submit"
                          size="sm"
                          disabled={saving || !evaluateDraft(editing.draft).valid}
                          data-testid="workflow-webhook-edit-save"
                        >
                          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          {t("workflowApi.webhook.save")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={saving}
                          onClick={() => setEditing(null)}
                          data-testid="workflow-webhook-edit-cancel"
                        >
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </form>
                  ) : null}
                  {journalFor === trigger.trigger_id ? (
                    <WebhookCallJournal
                      organizationId={organizationId}
                      trigger={trigger}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={pending !== null}
        title={
          pending?.kind === "rotate"
            ? t("workflowApi.webhook.rotateTitle")
            : t("workflowApi.webhook.revokeTitle")
        }
        description={
          pending?.kind === "rotate"
            ? t("workflowApi.webhook.rotateDescription")
            : t("workflowApi.webhook.revokeDescription")
        }
        confirmText={
          pending?.kind === "rotate"
            ? t("workflowApi.webhook.rotate")
            : t("workflowApi.webhook.revoke")
        }
        cancelText={t("common.cancel")}
        variant={pending?.kind === "revoke" ? "destructive" : "default"}
        disabled={busy !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        onConfirm={() => {
          if (pending) void runAction(pending.trigger, pending.kind);
        }}
        confirmTestId="workflow-webhook-confirm"
      />
    </section>
  );
}

function WebhookMappingSummary({ trigger }: { trigger: WorkflowTrigger }) {
  const { t } = useTranslation();
  const mapping = trigger.input_mapping ?? { mode: "body" };
  const fields = Object.entries(mapping.fields ?? {});
  const constants = Object.entries(mapping.constants ?? {});
  return (
    <div
      className="space-y-1 text-xs text-muted-foreground"
      data-testid={`workflow-webhook-mapping-${trigger.trigger_id}`}
    >
      <p>
        <span>{t("workflowApi.webhook.mappingLabel")} : </span>
        {mapping.mode === "pointers"
          ? t("workflowApi.webhook.mappingPointers")
          : t("workflowApi.webhook.mappingBody")}
      </p>
      {fields.length > 0 ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1">
          {fields.map(([field, pointer]) => (
            <li key={field}>
              <code>{field}</code> ← <code>{pointer || "/"}</code>
            </li>
          ))}
        </ul>
      ) : null}
      {constants.length > 0 ? (
        <div>
          <p>{t("workflowApi.webhook.constantsLabel")} :</p>
          <ul
            className="flex flex-wrap gap-x-4 gap-y-1"
            data-testid={`workflow-webhook-constants-${trigger.trigger_id}`}
          >
            {constants.map(([key, value]) => (
              <li key={key}>
                <code>{key}</code> = <code>{formatConstantValue(value)}</code>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Fields shared by creation and edition: name, input mapping (pointers and
 * constants), response mode and per-minute limit.
 */
function WebhookTriggerFields({
  draft,
  onChange,
  testIdPrefix,
}: {
  draft: TriggerDraft;
  onChange: (next: TriggerDraft) => void;
  testIdPrefix: string;
}) {
  const { t } = useTranslation();
  const evaluated = evaluateDraft(draft);
  const set = <K extends keyof TriggerDraft>(key: K, value: TriggerDraft[K]) =>
    onChange({ ...draft, [key]: value });
  const setConstant = (index: number, patch: Partial<ConstantRow>) =>
    set(
      "constants",
      draft.constants.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-foreground">
            {t("workflowApi.webhook.nameLabel")}
          </span>
          <Input
            value={draft.name}
            maxLength={120}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              set("name", event.target.value)
            }
            placeholder={t("workflowApi.webhook.namePlaceholder")}
            data-testid={`${testIdPrefix}-name`}
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-foreground">
            {t("workflowApi.webhook.rateLimitLabel")}
          </span>
          <Input
            type="number"
            min={1}
            max={WEBHOOK_RATE_LIMIT_MAX}
            value={draft.rateLimit}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              set("rateLimit", event.target.value)
            }
            aria-invalid={!evaluated.rateLimitValid}
            data-testid={`${testIdPrefix}-rate-limit`}
          />
          <span
            className={
              evaluated.rateLimitValid
                ? "block text-xs text-muted-foreground"
                : "block text-xs text-destructive"
            }
          >
            {t("workflowApi.webhook.rateLimitHint", {
              max: WEBHOOK_RATE_LIMIT_MAX,
              default: WEBHOOK_RATE_LIMIT_DEFAULT,
            })}
          </span>
        </label>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-foreground">
            {t("workflowApi.webhook.mappingLabel")}
          </span>
          <select
            value={draft.mappingMode}
            onChange={(event) =>
              set("mappingMode", event.target.value as WorkflowTriggerInputMapping["mode"])
            }
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            data-testid={`${testIdPrefix}-mapping`}
          >
            <option value="body">{t("workflowApi.webhook.mappingBody")}</option>
            <option value="pointers">{t("workflowApi.webhook.mappingPointers")}</option>
          </select>
        </label>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-foreground">
            {t("workflowApi.webhook.responseLabel")}
          </span>
          <select
            value={draft.responseMode}
            onChange={(event) =>
              set("responseMode", event.target.value as WorkflowTriggerResponseMode)
            }
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            data-testid={`${testIdPrefix}-response`}
          >
            <option value="async">{t("workflowApi.webhook.responseAsync")}</option>
            <option value="sync">{t("workflowApi.webhook.responseSync")}</option>
          </select>
        </label>
      </div>
      {draft.mappingMode === "pointers" ? (
        <>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-foreground">
              {t("workflowApi.webhook.pointersLabel")}
            </span>
            <textarea
              value={draft.pointerLines}
              onChange={(event) => set("pointerLines", event.target.value)}
              rows={4}
              placeholder={"email = /contact/email\npriority = /ticket/priority"}
              className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
              data-testid={`${testIdPrefix}-pointers`}
            />
            <span className="block text-xs text-muted-foreground">
              {evaluated.pointers.invalidLine
                ? t("workflowApi.webhook.pointersInvalid", {
                    line: evaluated.pointers.invalidLine,
                  })
                : t("workflowApi.webhook.pointersHint")}
            </span>
          </label>
          <fieldset className="space-y-2 text-sm">
            <legend className="font-medium text-foreground">
              {t("workflowApi.webhook.constantsLabel")}
            </legend>
            <p className="text-xs text-muted-foreground">
              {t("workflowApi.webhook.constantsHint")}
            </p>
            {draft.constants.map((row, index) => (
              <div key={index} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  value={row.key}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setConstant(index, { key: event.target.value })
                  }
                  placeholder={t("workflowApi.webhook.constantKeyPlaceholder")}
                  aria-label={t("workflowApi.webhook.constantKey", { index: index + 1 })}
                  className="font-mono text-xs sm:max-w-[14rem]"
                  data-testid={`${testIdPrefix}-constant-key-${index}`}
                />
                <Input
                  value={row.value}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setConstant(index, { value: event.target.value })
                  }
                  placeholder={t("workflowApi.webhook.constantValuePlaceholder")}
                  aria-label={t("workflowApi.webhook.constantValue", { index: index + 1 })}
                  className="font-mono text-xs"
                  data-testid={`${testIdPrefix}-constant-value-${index}`}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={t("workflowApi.webhook.constantRemove", { index: index + 1 })}
                  onClick={() =>
                    set(
                      "constants",
                      draft.constants.filter((_, i) => i !== index),
                    )
                  }
                  data-testid={`${testIdPrefix}-constant-remove-${index}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            ))}
            {evaluated.constants.invalidKey !== null ? (
              <p role="alert" className="text-xs text-destructive">
                {t("workflowApi.webhook.constantInvalid", {
                  key: evaluated.constants.invalidKey,
                })}
              </p>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => set("constants", [...draft.constants, { key: "", value: "" }])}
              data-testid={`${testIdPrefix}-constant-add`}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("workflowApi.webhook.constantAdd")}
            </Button>
          </fieldset>
        </>
      ) : null}
      {draft.responseMode === "sync" ? (
        <label className="block max-w-xs space-y-1 text-sm">
          <span className="font-medium text-foreground">
            {t("workflowApi.webhook.syncTimeoutLabel")}
          </span>
          <Input
            type="number"
            min={1}
            max={30}
            value={draft.syncTimeout}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              set("syncTimeout", event.target.value)
            }
            data-testid={`${testIdPrefix}-sync-timeout`}
          />
        </label>
      ) : null}
    </>
  );
}

function WebhookCallJournal({
  organizationId,
  trigger,
}: {
  organizationId: string;
  trigger: WorkflowTrigger;
}) {
  const { t } = useTranslation();
  const formatDate = useDateFormatter();
  const callsQuery = useQuery({
    queryKey: ["workflow-trigger-calls", organizationId, trigger.trigger_id],
    queryFn: () =>
      workflowTriggersAPI.listCalls(
        organizationId,
        trigger.application_id,
        trigger.trigger_id,
      ),
  });
  const calls: WorkflowTriggerCall[] = callsQuery.data ?? [];
  if (callsQuery.isLoading) {
    return <p className="text-xs text-muted-foreground">{t("common.loading")}</p>;
  }
  if (callsQuery.isError) {
    return (
      <p role="alert" className="text-xs text-destructive">
        {t("workflowApi.webhook.journalFailed")}
      </p>
    );
  }
  if (calls.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("workflowApi.webhook.journalEmpty")}
      </p>
    );
  }
  return (
    <ul
      className="space-y-1 rounded-md bg-muted p-3 text-xs"
      data-testid={`workflow-webhook-calls-${trigger.trigger_id}`}
    >
      {calls.map((call) => (
        <li key={call.id} className="flex flex-wrap items-center gap-2">
          <span>{formatDate(call.received_at)}</span>
          <Badge
            variant={
              outcomeVariant[call.outcome as keyof typeof outcomeVariant] ??
              "outline"
            }
          >
            {t(`workflowApi.webhook.outcome.${call.outcome || "rejected"}`)}
          </Badge>
          <span>HTTP {call.http_status}</span>
          {call.error_code ? <code>{call.error_code}</code> : null}
          {call.execution_id ? (
            <code>{t("workflowApi.webhook.execution", { id: call.execution_id })}</code>
          ) : null}
          {call.count > 1 ? (
            <span>{t("workflowApi.webhook.aggregated", { count: call.count })}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
